import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash, PencilSimple, Bank as BankIcon } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const fmt = (n) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(n || 0))}`;
const ACCOUNT_TYPE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank', label: 'Bank Account' },
  { value: 'payment_gateway', label: 'Payment Gateway' },
];
const ACCOUNT_TYPE_LABELS = {
  cash: 'Cash',
  bank: 'Bank Account',
  payment_gateway: 'Payment Gateway',
  savings: 'Savings',
  current: 'Current',
  fixed: 'Fixed Deposit',
};

export default function BankAccountsPage() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ bank_name: '', account_number: '', account_type: 'bank', balance: '', branch: '' });

  const load = () => { api.get('/banks').then(r => setBanks(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await api.post('/banks', { ...form, balance: parseFloat(form.balance) || 0 });
    setDialogOpen(false);
    setForm({ bank_name: '', account_number: '', account_type: 'bank', balance: '', branch: '' });
    load();
  };

  const handleDelete = async (id) => { await api.delete(`/banks/${id}`); load(); };
  const totalBalance = banks.reduce((s, b) => s + b.balance, 0);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="banks-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>Accounts</h1>
          <p className="text-[#5C5A56] text-sm mt-1">Manage cash, bank accounts, and payment gateways in one place</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-bank-btn" className="bg-[#1E3F2D] text-white font-semibold rounded-xl px-5 py-2.5 hover:bg-[#29563E] transition-colors active:scale-95 flex items-center gap-2 text-sm"><Plus size={18} weight="bold" /> Add Account</button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#E8E6E1] rounded-2xl">
            <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Add Money Source</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <label htmlFor="account-name" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Account Name</label>
                <input id="account-name" name="bank_name" data-testid="bank-name-input" type="text" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="eSewa Wallet / Cash in Hand / Nabil Bank" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="account-number" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Account / Wallet Number</label>
                  <input id="account-number" name="account_number" data-testid="account-number-input" type="text" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="Optional" />
                </div>
                <div>
                  <label htmlFor="account-type" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Source Type</label>
                  <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                    <SelectTrigger id="account-type" name="account_type" className="w-full border-[#E8E6E1] rounded-lg" data-testid="account-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="account-balance" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Balance (Rs.)</label>
                  <input id="account-balance" name="balance" data-testid="balance-input" type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="50000" />
                </div>
                <div>
                  <label htmlFor="account-branch" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Branch</label>
                  <input id="account-branch" name="branch" type="text" value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all" placeholder="Optional" />
                </div>
              </div>
              <button data-testid="submit-bank-btn" type="submit" className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-95">Add Account</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Total Balance */}
      <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56]">Total Available Balance</p>
        <p className="text-3xl font-bold font-mono-numbers text-[#1E3F2D] mt-1" data-testid="total-bank-balance">{fmt(totalBalance)}</p>
        <p className="text-xs text-[#5C5A56] mt-1">Across {banks.length} source{banks.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Bank List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bank-list">
        {loading ? <div className="col-span-2 p-8 text-center"><div className="w-6 h-6 border-2 border-[#1E3F2D] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        : banks.length === 0 ? <div className="col-span-2 bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-8 text-center text-[#5C5A56] text-sm">No accounts added yet</div>
        : banks.map(b => (
          <div key={b.id} className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-5 hover:shadow-md transition-all duration-300" data-testid={`bank-${b.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1E3F2D]/10 rounded-xl flex items-center justify-center"><BankIcon size={20} weight="duotone" className="text-[#1E3F2D]" /></div>
                <div>
                  <p className="text-base font-semibold text-[#1C1B1A]">{b.bank_name}</p>
                  <p className="text-xs text-[#5C5A56]">{ACCOUNT_TYPE_LABELS[b.account_type] || b.account_type} {b.branch && `- ${b.branch}`}</p>
                </div>
              </div>
              <button onClick={() => handleDelete(b.id)} className="text-[#5C5A56] hover:text-[#A63C24] transition-colors p-1"><Trash size={16} /></button>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xs text-[#5C5A56]">Balance</p>
                <p className="text-xl font-bold font-mono-numbers text-[#1E3F2D]">{fmt(b.balance)}</p>
              </div>
              {b.account_number && <p className="text-xs text-[#5C5A56] font-mono-numbers">A/C: {b.account_number}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
